import React from 'react';
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useNonValidatedUser } from '../plugins/auth/Authentication';
import { Logger } from '../plugins/logger/Logger';
import { VerifyEmailPage } from '../pages/VerifyEmailPage';
import { Register } from '../pages/Register';
import { AcceptInvitation } from '../pages/AcceptInvitation';
import { MainContainer } from '../components/atoms/layouts/MainContainer';
import { FullScreenSize } from '../components/atoms/layouts/FullScreenSize';
import { Centered } from '../components/atoms/layouts/Centered';
import { Loader } from '../components/molecules/loader/Loader';
import { getSubscriptionPlanQueryOptions } from '../rest-api/queries/sharedQueries';

export const Route = createFileRoute('/_private')({
  // One-shot silent token refresh before render: an unverified email_verified
  // claim may just be a stale token, so force a fresh one and let the
  // component decide reactively. A refresh failure falls through to the
  // verify-email screen instead of an error page.
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated && !context.auth.isLoading) {
      await context.auth.login();
      return;
    }

    if (!context.auth.verified()) {
      try {
        await context.auth.forceNewToken();
      } catch (e) {
        Logger.trackException(e as Error);
      }
    }
  },
  pendingComponent: () => <FullScreenLoader label="Verifying email..." />,
  component: PrivateGate,
});

const FullScreenLoader = ({ label }: { label: string }) => (
  <FullScreenSize>
    <Centered tryGoldenCenter>
      <Loader label={label} />
    </Centered>
  </FullScreenSize>
);

/**
 * Email-verification gate: beforeLoad has already forced a token refresh, so
 * this is a pure reactive check. The verify-email screen retries the refresh
 * on window focus; the Auth0 context update re-renders this gate and opens it.
 */
function PrivateGate() {
  const { auth } = Route.useRouteContext();
  const user = useNonValidatedUser();

  if (!auth.isAuthenticated || auth.isLoading) {
    return <FullScreenLoader label="Authenticating..." />;
  }

  if (!user.verified()) {
    return (
      <VerifyEmailPage
        email={user.email}
        onFocus={() => {
          user.forceNewToken().catch((e) => Logger.trackException(e as Error));
        }}
      />
    );
  }

  return <RegisteredGate />;
}

/**
 * Registration/invitation gate (formerly the top of PrivateRoutes): renders
 * Register or AcceptInvitation in place — without changing the URL — until the
 * user is fully onboarded.
 */
function RegisteredGate() {
  const { apiClient } = Route.useRouteContext();
  const navigate = useNavigate();
  // Intentionally shares the ['me'] query key with getMeQueryOptions (sharedQueries.ts):
  // this getMeOptional query (staleTime/gcTime 0) seeds the cache that the rest of the
  // app reads with staleTime Infinity. Pre-existing behavior kept as-is.
  const {
    data: me,
    refetch: refetchMe,
    isPending: meIsPending,
  } = useQuery({
    queryKey: ['me'] as const,
    queryFn: async () => await apiClient.getMeOptional(),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const { isPending: planIsPending } = useQuery({
    ...getSubscriptionPlanQueryOptions(apiClient),
    enabled: Boolean(me),
  });

  const toProjectsWithWelcome = () => navigate({ to: '/project', search: { welcome: true } });

  if (meIsPending || (me !== null && planIsPending)) {
    return <FullScreenLoader label="Loading..." />;
  }

  if (me === null) {
    const register = () => {
      refetchMe()
        .then(() => toProjectsWithWelcome())
        .catch((err) => console.error(err));
      void toProjectsWithWelcome();
    };

    return <Register onRegister={register} portalApiClient={apiClient} />;
  }

  if (me?.inviteState !== 'Accepted') {
    const acceptInvitation = () => {
      refetchMe()
        .then(() => toProjectsWithWelcome())
        .catch((err) => console.log(err));
      void toProjectsWithWelcome();
    };
    return <AcceptInvitation onAccepted={acceptInvitation} portalApiClient={apiClient} />;
  }

  return (
    <MainContainer>
      <Outlet />
    </MainContainer>
  );
}
